import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useFlag, useManyRefs } from '@carnica/utils/hooks';
import { useCallback, useEffect, useRef } from 'react';
import { InputKeyCodes, getDataByEvent, getOnlyNumbersFromString, invalidKeys, isEqual, isNumeric, replaceCharAtIndex } from "./utils";
export var useCodeInput = function useCodeInput(params) {
  var _params$codeLength = params.codeLength,
    codeLength = _params$codeLength === void 0 ? 6 : _params$codeLength,
    _params$value = params.value,
    valueProp = _params$value === void 0 ? '' : _params$value,
    onFocusProp = params.onFocus,
    onChangeProp = params.onChange,
    onBlurProp = params.onBlur,
    onKeyDownProp = params.onKeyDown,
    autoFocus = params.autoFocus;
  var length = Math.max(codeLength, 1);
  var value = valueProp.replace(/\D/g, '').slice(0, length);
  var actualValue = useRef(value);
  var refs = useManyRefs(length);
  var _useFlag = useFlag(),
    _useFlag2 = _slicedToArray(_useFlag, 2),
    isFocused = _useFlag2[0],
    setIsFocused = _useFlag2[1];
  var onChange = useCallback(function (e) {
    var data = getDataByEvent(e);
    if (!isNumeric(data.value) || value.length === length) {
      return;
    }
    var newValue = replaceCharAtIndex(data.value.charAt(data.value.length - 1), value, data.index + data.value.length - 1);
    onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(newValue, e);
    actualValue.current = newValue;
    if (data.value) {
      var _refs$activeIndex$cur;
      var activeIndex = Math.min(data.index + 1, length - 1);
      (_refs$activeIndex$cur = refs[activeIndex].current) === null || _refs$activeIndex$cur === void 0 ? void 0 : _refs$activeIndex$cur.focus();
    }
  }, [value, onChangeProp, length]);
  var onFocus = useCallback(function (e) {
    var _getDataByEvent = getDataByEvent(e),
      index = _getDataByEvent.index;
    if (!isFocused) {
      var _refs$current;
      setIsFocused.on();
      (_refs$current = refs[value ? Math.min(value.length, index) : 0].current) === null || _refs$current === void 0 ? void 0 : _refs$current.focus();
      onFocusProp === null || onFocusProp === void 0 ? void 0 : onFocusProp(value, e);
      return;
    }
    if (!actualValue.current.charAt(Math.max(index - 1, 0))) {
      var _refs$Math$min$curren;
      (_refs$Math$min$curren = refs[Math.min(actualValue.current.length, length - 1)].current) === null || _refs$Math$min$curren === void 0 ? void 0 : _refs$Math$min$curren.focus();
    }
  }, [onFocusProp, isFocused, value, length]);

  // Обработчик событий с клавиатуры, обрабатывает следущие кейсы
  // - перемещение назад и вперед по символам между инпутами
  // - обработка удаления символов и фокус на нужном инпуте
  // - обработка невалидных значений с клавиатуры
  var onKeyDown = useCallback(function (e) {
    var _Object$values;
    onKeyDownProp === null || onKeyDownProp === void 0 ? void 0 : onKeyDownProp(e);

    // TS счиатет что в KeyboardEvent<HTMLInputElement> у target нет value и dataset
    var _getDataByEvent2 = getDataByEvent(e),
      index = _getDataByEvent2.index;
    if (invalidKeys.includes(e.key) || (_Object$values = Object.values(InputKeyCodes)) !== null && _Object$values !== void 0 && _Object$values.includes(e.key)) {
      e.preventDefault();
    }
    if (isEqual(e.key, InputKeyCodes.BACKSPACE)) {
      var _refs$Math$max$curren;
      onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(value === null || value === void 0 ? void 0 : value.slice(0, value.length - 1), e);
      actualValue.current = value === null || value === void 0 ? void 0 : value.slice(0, value.length - 1);
      (_refs$Math$max$curren = refs[Math.max(index - 1, 0)].current) === null || _refs$Math$max$curren === void 0 ? void 0 : _refs$Math$max$curren.focus();
      return;
    }
    if (isEqual(e.key, InputKeyCodes.LEFT_ARROW)) {
      var _refs$Math$max$curren2;
      (_refs$Math$max$curren2 = refs[Math.max(index - 1, 0)].current) === null || _refs$Math$max$curren2 === void 0 ? void 0 : _refs$Math$max$curren2.focus();
      return;
    }
    if (isEqual(e.key, InputKeyCodes.RIGHT_ARROW)) {
      var _refs$Math$min$curren2;
      (_refs$Math$min$curren2 = refs[Math.min(index + 1, length - 1)].current) === null || _refs$Math$min$curren2 === void 0 ? void 0 : _refs$Math$min$curren2.focus();
    }
  }, [onKeyDownProp, onChangeProp, value, length]);
  var onBlur = function onBlur(e) {
    if (!refs.find(function (ref) {
      return ref.current === e.relatedTarget;
    })) {
      onBlurProp === null || onBlurProp === void 0 ? void 0 : onBlurProp(value, e);
      setIsFocused.off();
    }
  };
  var onPaste = function onPaste(e) {
    e.preventDefault();
    var newValue = getOnlyNumbersFromString(e.clipboardData.getData('text'), length);
    if (newValue) {
      var _refs$current2;
      actualValue.current = newValue;
      onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(newValue, e);
      (_refs$current2 = refs[newValue.length - 1].current) === null || _refs$current2 === void 0 ? void 0 : _refs$current2.focus();
    }
  };
  useEffect(function () {
    if (autoFocus) {
      var _refs$0$current;
      (_refs$0$current = refs[0].current) === null || _refs$0$current === void 0 ? void 0 : _refs$0$current.focus();
    }
  }, []);
  return {
    onChange: onChange,
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    refs: refs,
    onBlur: onBlur,
    onPaste: onPaste,
    value: value,
    length: length
  };
};